import Handlebars from "handlebars";
import "./extensions/HandlebarsExtensions.js";
import Commons from "./Commons.js";

const global_store = {};

/**
 * Generisches System für Handlebars Templates
 *
 * Vorteile:
 *
 * - Einheitliche Datei-Struktur für Template in allen Modulen
 * - Standard-Template können in Apps auf einfache Weise überschrieben werden. Die Änderungen sind dann im global im ganzen Holodeck gültig.
 * - Durchgehend "absolute" Pfade für Template-Namen entsprechend der Modul bzw. Ordnerstruktur und erleichten die Suche im Code
 *
 * Als Beispiel siehe oc-ui/dialogs/dialogsBootstrap3.js und oc-ui/hbs.
 *
 * Vorgehen:
 *
 * 1. Man erstelle im Modul einen Ordner hbs
 * 2. Darin eine Datei hbs/HBS.js mit folgenden Inhalt
 *    <pre><code>
 *    import MainHBS from 'oc-commons/MainHBS.js';
 *    // Zentraler Zugriff auf alle Handlebars-Templates dieses Moduls
 *    // require.context muss hier übergeben werden, um Webpack einen Zugriffspunkt auf dieses konkrete
 *    // Modul-Verzeichnis zu geben, damit zur Build-Zeit alle HBS-Files registriert werden können
 *    // Parameter im Beispiel:
 *    // 10 ist die Priorität, bitte anpassen. Dient zum Festlegen der Überschreibreihenfolge bei gleichem Namen. Der höchste gewinnt.
 *    // 'oc-mein-modul-name' ist der Name des Moduls, dient nur zu Debugzwecken.
 *    export default MainHBS(require.context(".", true),10,'oc-mein-modul-name');
 *    </code></pre>
 * 3. Dann erstellt man im hbs-Ordner einen Unterordner, der gleich dem Modulnamen ist, z.B. hbs/oc-ui
 *    In oc-ui packt man seine ganzen Templates, gerne in Unterordnern, die der Ordnerstruktur im Modul entsprechen.
 *    Wenn man also z.B. seinen Code im Order oc-ui/dialogs hat, dann kommen die Templates nach oc-ui/hbs/oc-ui/dialogs/
 *    Das erste oc-ui ist in diesem Beispiel das modul-Verzeichnis selbst.
 * 4. Dann lädt man die Templates über die Modul-HBS.js:
 *    <pre><code>
 *    import HBS from "../hbs/HBS.js";
 *    var dialog = $(HBS('oc-ui/dialogs/error.hbs')({message}));
 *    </code></pre>
 *    Der Zugriff auf die Templates ist gecached.
 * 5. Wenn man in der App ein Template ersetzen möchte, legt man dort auf die gleiche Weise einen hbs-Ordner
 *    und eine HBS.js an und legt ein Template mit dem gleichen Pfad an (in diesem Fall also MyApp/hbs/oc-ui/dialogs/error.hbs).
 *    Wenn man dann in der App HBS.js als letztes importiert, dann wird die Standard error.hbs durch die App-Version global
 *    ersetzt.
 *
 * @param {*} req der lokale require.content
 * @param {number} modulePriority Zahl, dient zum Festlegen der Überschreibreihenfolge bei gleichem Namen. Der höchste gewinnt. Der App gibt man z.B. 100, die oc-Module habe in der Regel Zehnerschritte
 * @param {string} [optionalModuleNameForDebugging='app']
 */
function MainHBS(req,modulePriority,optionalModuleNameForDebugging='app') {

    req.keys().filter(filepath=>/\.hbs$/.test(filepath)).forEach(filepath =>
        addHbs(filepath,filepath=>req(filepath),modulePriority,optionalModuleNameForDebugging));

    return local_hbs;

}

/**
 * @param {number} modulePriority
 */
function addHbs(filepath,_templateFn,modulePriority,optionalModuleNameForDebugging) {

    const absolutePath = filepath.substring(2);
    const filename = filepath.substringAfterLast('/');
    const templateFn = (fp) => {
        let template = _templateFn(fp);
        return (template != null && !_.isString(template) && template.default) ? template.default : template;
    }

    const value = Commons.cacheIdentity(() => {
        //console.log("load ",filepath);

        let template = templateFn(filepath);
        const compiledFunction = Handlebars.compile(template);
        // falls das uncompilierte Template benötigt wird (z.B. für {{{{raw}}}})
        compiledFunction.template = template;
        //console.log(template);
        return compiledFunction;
    });

    const partial = templateFn(filepath);
    storeIfHigherPrio(absolutePath,value,partial,modulePriority,optionalModuleNameForDebugging);
    storeIfHigherPrio(filename,value,partial,modulePriority,optionalModuleNameForDebugging);

}

function local_hbs (path) {
    const entry = global_store[path];
    if (entry) {
        return entry.value();
    }
    throw "no template found with path "+path;
}

function storeIfHigherPrio(path,value,partial,modulePriority,moduleName) {
    const entry = global_store[path];

    if (entry && entry.modulePriority>modulePriority) {
        return;
    }

    if (!entry) {
        global_store[path]={value,modulePriority,moduleName};
    } else {
        entry.value = value;
        entry.moduleName = moduleName;
    }

    Handlebars.registerPartial(path,partial);

    // zum Debuggen von Reihenfolge-Probleme hilft diese Logausgabe
    // console.log('HBS ',moduleName+'/'+path);
}

local_hbs.includeTemplatesFromDom = function(modulePriority,optionalModuleNameForDebugging) {
    const templateScripts = $('script[type="text/x-handlebars-template"][data-path]');
    templateScripts.forEach(scriptElement=>{
        addHbs(
            scriptElement.attr('data-path'),
            ()=>scriptElement.text(),
            modulePriority,
            optionalModuleNameForDebugging);
    });
}

//Doms.handlebarsRegisterSafe({
//    HBS: function(path,...args) {
//        const template = local_hbs(path);
//        console.log(path,args,template);
//        return template();
//    }
//});

export default MainHBS;
